import { CardMetric, DeltaUnit, DistributionSegment, MetricDelta, MetricFormat } from '../types';
import { confidenceFromRow, MetricConfidence } from './confidence';
// `asNumber`/`asString` are the cell coercions `distributionFromEnvelope` reads its rows through;
// they moved into `metabaseTable.ts` with the rest of the envelope layer (PLA-8828).
import {
  asNumber,
  asString,
  columnIndexOf,
  MetricEnvelope,
  readerFor,
  tableFrom
} from './metabaseTable';
import { secondaryFor } from './secondaryDelta';
import { readingRowsOnly, WTR_RESULT_ROLE, wtrFromRow, WtrReason } from './wtr';

// The envelope types moved to `metabaseTable.ts` when Customer 360 brought two further column
// schemas onto the same endpoint (PLA-8828). Re-exported so every existing importer of this module
// is unchanged.
export type { MetabaseColumn, MetricEnvelope } from './metabaseTable';
export { COMPLETED_STATUS } from './metabaseTable';

// Extra numeric columns a metric's own Metabase question returns ALONGSIDE the six the contract
// names, listed by name so they stop being dropped on the floor.
//
// A named allowlist, not "read every column": an unrecognised column becoming a figure is the same
// class of failure as a positional read, and extending this array is the deliberate act that admits
// the next one. `eligible_customer_count` is the first — O-COM-001 returns it in its own row
// (2,116 against a `metric_value` of 2,500 on UAT `generic`), and the card's Eligible Coverage tile
// is that quotient and nothing else. PLA-8815.
// `average_active_holdings` (1.633182) and `multi_product_customer_count` (1158) ride in on
// O-COM-032's rows the same way, and are POPULATION-level rather than per-row: both repeat
// identically on all four of that metric's calculated band rows on UAT `generic`, verified against
// the live response 2026-09-03. `distributionFromEnvelope` still checks that agreement per response
// rather than trusting row 0, because a disagreement would mean they are per-band after all.
// `high_risk_customer_count` is the same population-level shape on O-COM-023's rows, and reads 0 on
// UAT `generic` (2026-09-03) — a measured zero, which is why it is admitted here rather than left
// out as "nothing to show": the response says nobody is high-risk this period, and that is a
// reading. PLA-8815.
// `clv_scoreable_customer_count` (1997) is the same population-level shape again, on O-COM-028's
// rows, identical across all four calculated band rows on UAT `generic` (2026-09-03). Its SIBLING
// `clv_scored_customer_count` (1788) is deliberately NOT allowlisted: that column is the band
// denominator, which the response's own `calculation_basis` states as "customers per current CLV
// band ... / scored customers * 100" and which 447 x 4 bands reproduces exactly. Admitting it would
// put two near-identical counts within reach of one tile labelled "Eligible Customers"; see the
// `current_predicted_customer_value` entry in `registry/metricIdentifiers.ts`.
export const EXTRA_NUMERIC_COLUMNS = [
  'eligible_customer_count',
  'average_active_holdings',
  'multi_product_customer_count',
  'high_risk_customer_count',
  'clv_scoreable_customer_count',
  'funnel_entering_customers',
  'funnel_final_stage_customers',
  // The Engagement Funnel step chain's own steps, from O-COM-013. PLA-8815.
  'funnel_notification_opened_customers',
  'funnel_notification_clicked_customers',
  'funnel_campaign_enrolled_customers',
  // Two more population-level counts their own scalar responses already carry, and which the tiles
  // labelled for them were declared `derived` around: `nrs_ready_customer_count` (624) on O-COM-026
  // and `handoff_blocked_count` (327) on O-COM-030, both UAT `generic` 2026-09-08. O-COM-030's
  // three sibling counts stay out -- `handoff_evaluated_count` and the two blocked-by-REASON
  // breakdowns are a different question from the one its card's two tiles ask. PLA-8815.
  'nrs_ready_customer_count',
  'handoff_blocked_count',
  // The FACTOR columns behind four governed scores -- the "top contributing factors" the catalogue
  // asks each score to display. Each is a MEAN over the scored population on a 0-1 scale, which is
  // why none of them is a candidate for a tile: they are the score's inputs, not readings of their
  // own. Direction is declared per factor in `registry/metricIdentifiers.ts` from each metric's
  // `governed_formula_expression`, never from these names. PLA-8815.
  'dews_mean_recency_drop',
  'dews_mean_frequency_drop',
  'dews_mean_engagement_drop',
  'dews_mean_negative_sentiment',
  'dews_mean_expectation_gap',
  'dews_mean_tenure_adjustment',
  'hss_mean_periodicity',
  'hss_mean_interval_consistency',
  'hss_mean_repeated_percentile',
  'hss_mean_tenure_norm',
  'nrs_mean_component_propensity',
  'nrs_mean_component_confidence',
  'nrs_mean_component_recency',
  'nrs_mean_component_reachability',
  'nrs_mean_component_no_fatigue',
  'nrs_mean_component_no_risk',
  // O-COM-021's two, the only factor set whose columns never reached `components`: the Expectation
  // Gap rail read an allowlist these names were absent from, so it rendered empty from every live
  // envelope. Both are answered -- `egs_mean_delayed_attempts` 1.209838 and
  // `egs_mean_negative_signals` 0.125065 on UAT `generic` 2026-09-18, against an EGS of 17.805936.
  // Unlike the means above these are NOT 0-1: the first is a count of attempts per customer, which
  // is why the rail ranks by magnitude rather than reading a scale off the block. PLA-8828.
  'egs_mean_delayed_attempts',
  'egs_mean_negative_signals',
  // O-COM-022's six severity drivers, in its formula's order. Two read `null` on UAT `generic`
  // (2026-09-08) and are withheld rather than zero; three read a calculated 0.0 and one 0.07, which
  // is a tenant with almost no fraud signal and a FAS of 3.32 to match. PLA-8815.
  'fas_mean_factor_velocity_z',
  'fas_mean_factor_amount_outlier',
  'fas_mean_factor_duplicate_issuance',
  'fas_mean_factor_device_ip',
  'fas_mean_factor_callback_mismatch',
  'fas_mean_factor_identity_conflict',
  // The Engagement Funnel's two remaining stages BY NAME. The chain drew them through
  // `funnel_entering_customers` / `funnel_final_stage_customers`, which are the entry and final
  // COHORTS -- equal to these two on UAT `generic` today, and not the same question. PLA-8815.
  'funnel_notification_delivered_customers',
  'funnel_campaign_action_completed_customers',
  // The Figma funnel's stages 06 / 07, on the same row as the five above.
  'funnel_outcome_issued_customers',
  'funnel_outcome_redeemed_customers',
  // O-COM-024's own count of customers behind its signals (0 on UAT `generic` 2026-09-08 -- a
  // measured zero). Its card's `Customers with Active Signals` tile was pointed at O-COM-025, which
  // answers nothing at all. PLA-8815.
  'opportunity_active_customer_count',
  // O-REV-008's three-way reconciliation split (2921 / 728 / 364 on UAT `generic`). A split of what
  // happened to the reversals THIS question reconciled, and NOT a partition of O-REV-001's total:
  // summing them into that total would report a figure no question measured. PLA-8815.
  'reversal_accepted_count',
  'reversal_retrying_count',
  'reversal_failed_retrying_count',
  // The two CLV AMOUNTS, one per CLV metric, population-level on every band row (206.985022 on
  // O-COM-028's four `current_clv_band` rows, 105.611043 on O-COM-029's four `predicted_clv_band`
  // rows -- UAT `generic` 2026-09-18). Neither response carried a currency column when this card's
  // two amount tiles were declared `derived` around their absence; both do now. PLA-8828.
  'clv_current_mean_value',
  'clv_predicted_mean_value',
  // O-COM-030's two blocked-by-REASON counts (52 and 49 on UAT `generic` 2026-09-18). Admitted for
  // the card's Top Blockers rail, NOT for its two KPI tiles: 52 + 49 is not the 327
  // `handoff_blocked_count` those tiles read, so the reasons are neither exhaustive nor exclusive
  // and must never be summed into a total. PLA-8828.
  'handoff_blocked_low_confidence_count',
  'handoff_blocked_missing_consent_count',
  // The parent totals the CI-06 / CI-07 / CI-08 overlays head with, and O-COM-030's other two
  // blocked-by-REASON counts (729 / 2,171 on O-COM-026; 1,220 / 1,635 / 243 / 66 on O-COM-030,
  // UAT banking 2026-10-07). The four reasons sum to 442 against 415 blocked: still not a
  // partition. PLA-8828.
  'nrs_evaluable_customer_count',
  'handoff_eligible_count',
  'handoff_evaluated_count',
  'handoff_blocked_incomplete_evidence_count',
  'handoff_blocked_dnn_active_count',
  // C-COM-011's entitlement counts, the supporting values beside its redemption rate (18 issued,
  // 12 redeemed, 6 expired for customer 4 on every industry, 2026-09-30). PLA-8828.
  'entitlements_issued_count',
  'entitlements_redeemed_count',
  'entitlements_expired_count'
] as const;

// One period's reading, with the six field names the contract states. This is a shape THIS FILE
// builds — it is not the wire format. perx-api forwards Metabase's raw result, which is positional
// `rows` plus a `cols` array of column metadata, so `rowsFromEnvelope` below resolves each field by
// looking its name up in `cols`. Rows arrive newest first (`ORDER BY period_end DESC`, no LIMIT).
// PLA-8815 — contract PLA-8817, plan `metabase-data-seam.md` § V.
export interface MetricResultRow {
  metric_value: number | null;
  calculation_status: string;
  reason_code: string | null;
  source_dependency_status: string | null;
  period_start: string;
  period_end: string;
  /**
   * One entry per `EXTRA_NUMERIC_COLUMNS` name, `null` where this metric's question does not return
   * that column — which is most metrics. Null rather than absent at ROW level for the same reason
   * `metric_value` is: the row states what the wire said about every name this pipeline knows, and
   * "asked for, not answered" is a fact worth carrying. `toMetricResult` drops the nulls.
   */
  components: Record<string, number | null>;
  /**
   * Present only where this metric's question declares the confidence columns -- 19 of the 65
   * identifiers the Customer Insights registry reads (see `data/confidence.ts`). `undefined`
   * rather than an object of nulls on the other 46, so "no confidence schema" stays
   * distinguishable from "confidence withheld". PLA-8815, PLA-8825 § confidence columns.
   */
  confidence?: MetricConfidence;
  /**
   * The wire's own discriminator for what the reading IS -- `reward`, `low`, `stage_3`, `at_risk`,
   * `not_blocked`, a date. Free-form, not a closed vocabulary, and carried as sent: several tiles
   * hardcode a stage or a tone the wire already answers, and none of them can stop until the column
   * reaches them. Spread rather than assigned, for the `confidence` reason below. PLA-8828.
   */
  value_state?: string | null;
  /**
   * What KIND of thing this row is about (`campaign`, `engagement_source`, `loyalty_account`) and
   * WHICH one (`cmp_751c43bcf989c1bf`, `reward:badge`). Six Customer 360 metrics answer with one row
   * per entity and the rest with a single row carrying `null` in both -- which is exactly what tells
   * a detail row apart from a previous PERIOD's row, since neither `period_end` nor `result_role`
   * separates them (every captured row is `result_role: current`). PLA-8828.
   */
  entity_type?: string | null;
  entity_ref?: string | null;
  /**
   * The pipeline's own sentence about why this reading came out as it did -- prose, not a code.
   * 32 of the 38 responding Customer 360 metrics carry one; no Layer 1 question declares the column.
   */
  calculation_basis?: string | null;
  /**
   * The governed formula and its version, stated by the wire so the tile stops encoding a
   * definition of its own. `formula_display_text` is deliberately NOT read: the column is present on
   * all 38 responding metrics and null in every one, so wiring it would add a field that has never
   * carried a value. PLA-8828.
   */
  governed_formula_expression?: string | null;
  governed_formula_version?: string | null;
  /**
   * `current`, `previous` or `wtr`, naming which arm of the response this row is. `current`/
   * `previous` are carried but not acted on: probing UAT with `previous_start_date`/
   * `previous_end_date` on four comparison-capable metrics returned `current` rows only, so there
   * is no previous reading to build a period delta against. `wtr` IS acted on — `rowsFromEnvelope`
   * below sorts such a row to the end alongside a `metric_status` row, so it can never become
   * `results[0]`, and parses it into `wtr` rather than treating it as a reading. PLA-8828, PLA-8815.
   */
  result_role?: string | null;
  /**
   * The wire's name for the row's entity or state, where the question declares it (Phase 1B):
   * `display_name` for C-COM-010/012, C-HYB-008 and O-HYB-013/014, `state_label`/`state_code` for
   * O-COM-003/005/006. Governed text, carried verbatim.
   */
  display_name?: string | null;
  state_code?: string | null;
  state_label?: string | null;
  /**
   * This row's "Why This Result" reason, present only when `result_role` is `'wtr'` AND the row
   * carries a `wtr_reason_code` — a `wtr` row with no code states "no reason fired", which is not a
   * reason. See `data/wtr.ts`. PLA-8815.
   */
  wtr?: WtrReason;
}

/**
 * Metabase's positional rows into named readings, indexed through `cols`.
 *
 * Read by NAME, never by position, and never against a fixed six: the contract states that the
 * column set is whatever each metric's own Metabase question defines, so a metric may return more
 * columns, fewer, or the same six in a different order. A positional read would silently transpose
 * two fields the first time a question is edited — and `metric_value` landing in `reason_code`'s
 * slot is a wrong number that renders perfectly.
 *
 * A missing column yields `null`, never a zero or an empty string standing in for one. A row with no
 * usable `metric_value` therefore falls through to the `unavailable` arm below, which is the same
 * treatment a row the warehouse could not calculate gets. PLA-8815.
 */
/**
 * The `entity_type` of a row that reports a metric's STATE rather than its reading.
 *
 * A question that UNIONs a detail row with a rollup-status row returns both at the same
 * `period_end`, both `result_role: current` — so `ORDER BY period_end DESC` cannot separate them
 * and which arrives first is the database's choice. Observed live on `api-uat.perxtech.io`: row 0
 * `entity_type: null` carrying a reading, row 1 `entity_type: metric_status` carrying null.
 * PLA-8825.
 */
const STATUS_ENTITY_TYPE = 'metric_status';

/**
 * The `dimension_type` Layer 1 puts on the SUMMARY row of a response that also carries a
 * distribution — and the opposite convention to `STATUS_ENTITY_TYPE` above, under a name that looks
 * identical.
 *
 * On Customer 360 a `metric_status` row is a statement ABOUT the response and never carries a
 * reading. On Layer 1 it is the response's own scalar: `O-COM-012` answers three
 * `engagement_source_family` rows with no `metric_value` and a fourth, `metric_status`, holding
 * `97.845601`; `O-COM-009` does the same with three `dews_band` rows and `30.762954`. Those rows
 * arrive LAST, so `results[0]` landed on a distribution row and both cards drew an em dash over a
 * live reading (`api-uat.perxtech.io`, tenant `generic`, 2026-09-18).
 *
 * Promoted only when it carries a number, which is what keeps the rule honest: `O-COM-028`'s
 * `metric_status` row is `enabled_not_aggregated` / `governed_non_scalar_grain` and declares no
 * `metric_value` column at all, so it stays exactly where it arrived and that response parses as it
 * always did. PLA-8828.
 */
const SUMMARY_DIMENSION_TYPE = 'metric_status';

/**
 * A Customer 360 response that answers PER ENTITY can also carry the response's own scalar, as the
 * one row with neither an `entity_type` nor an `entity_ref`.
 *
 * C-COM-023 does exactly that: four rows typed `product_family` / `account_balance`, and a fifth,
 * untyped, stating 2 - the holding count, which its own note calls "standardized two-account holding
 * composition; Current A/C=1, Savings A/C=1". That row arrives FIFTH, so `results[0]` landed on a
 * `product_family` row and the Holdings ring drew 1 where the response said 2 (api-uat.perxtech.io,
 * tenant `digibank`, customer 4, 2026-09-22).
 *
 * Promoted only where the response actually answers per entity, and only when the untyped row
 * carries a number. A scalar metric -- whose every row is untyped, and every Layer 1 question, which
 * declares no `entity_type` column at all -- has no entity-typed row to compare against, so every
 * row keeps the rank and the incoming order it has always had. PLA-8828.
 */
const responseScalarFirst = (
  rows: unknown[][],
  read: ReturnType<typeof readerFor>
): ((row: unknown[]) => boolean) => {
  const answersPerEntity = rows.some(row => Boolean(read.text(row, 'entity_type')));

  return (row: unknown[]): boolean =>
    answersPerEntity &&
    !read.text(row, 'entity_type') &&
    !read.text(row, 'entity_ref') &&
    typeof read.number(row, 'metric_value') === 'number';
};

// Where a row sorts among its siblings: the summary reading first, ordinary rows next, then the
// per-entity detail rows, any other `result_role`, and a status or `wtr` row last. Equal ranks keep
// their incoming order.
const SUMMARY_FIRST = -1;
const READING_RANK = 0;
const DETAIL_RANK = 1;
const OTHER_ROLE_RANK = 2;
const STATUS_LAST = 3;

const CURRENT_RESULT_ROLE = 'current';
const NON_SCALAR_STATUS = 'enabled_not_aggregated';

export const rowsFromEnvelope = (envelope: MetricEnvelope): MetricResultRow[] => {
  const { cols, rows } = tableFrom(envelope);
  // Indexes `cols` once per envelope, exactly as the hand-rolled `indexOf`/`cell` pair it replaces
  // did; the three Customer 360 schemas read through the same helper (PLA-8828).
  const read = readerFor(cols);

  // Readings first, status rows after, each group keeping its incoming order. Every consumer takes
  // `results[0]` as the current reading, so a status row sorted ahead of a calculated one empties a
  // card that has data. Not a filter: a metric whose ONLY row is a status row still has to reach
  // `toCardMetric`, or its `calculation_status` and `reason_code` never reach the reader.
  //
  // `entity_type` reaches the row below as well as sorting it here, spread like every other Customer
  // 360 column so a Layer 1 question -- which declares none of them -- produces the identical object
  // 27 shipped cards assert on. PLA-8828.
  //
  // A `result_role: 'wtr'` row gets the SAME treatment as a status row and for the same reason: it
  // carries no `metric_value`, so a wtr row sorted ahead of the real reading would become
  // `results[0]` and empty a card that has data. PLA-8815.
  //
  // And Layer 1's summary row is sorted the other way, to the FRONT — see `SUMMARY_DIMENSION_TYPE`.
  // Customer 360's own scalar row goes to the front for the same reason — see
  // `responseScalarFirst`.
  const isResponseScalar = responseScalarFirst(rows, read);
  const hasValue = (row: unknown[]): boolean =>
    typeof read.number(row, 'metric_value') === 'number';
  const roleOf = (row: unknown[]): string | null => read.text(row, 'result_role');
  // The Phase 1B handoff's rule for the main value: `result_role = 'current'` and `entity_type IS
  // NULL`, never row 0. A mixed-grain metric can put its `enabled_not_aggregated` grain marker FIRST,
  // so once the response carries such a reading the marker goes to the back with the status rows.
  // Without one the marker keeps its place, and a pure distribution parses as it always did.
  const hasUntypedReading = rows.some(
    row =>
      hasValue(row) &&
      !read.text(row, 'entity_type') &&
      (roleOf(row) === null || roleOf(row) === CURRENT_RESULT_ROLE)
  );

  const rankOf = (row: unknown[]): number => {
    if (
      read.text(row, 'entity_type') === STATUS_ENTITY_TYPE ||
      roleOf(row) === WTR_RESULT_ROLE ||
      (hasUntypedReading &&
        read.text(row, 'calculation_status') === NON_SCALAR_STATUS &&
        !hasValue(row))
    ) {
      return STATUS_LAST;
    }

    if (roleOf(row) !== null && roleOf(row) !== CURRENT_RESULT_ROLE) {
      return OTHER_ROLE_RANK;
    }

    if (isResponseScalar(row)) {
      return SUMMARY_FIRST;
    }

    if (read.text(row, 'entity_type')) {
      return DETAIL_RANK;
    }

    return read.text(row, 'dimension_type') === SUMMARY_DIMENSION_TYPE && hasValue(row)
      ? SUMMARY_FIRST
      : READING_RANK;
  };

  const ordered = rows
    .map((row, index) => ({ row, index, rank: rankOf(row) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(entry => entry.row);

  return ordered.map(row => {
    const confidence = confidenceFromRow(read, row);
    const valueState = read.text(row, 'value_state');
    const entityType = read.text(row, 'entity_type');
    const entityRef = read.text(row, 'entity_ref');
    const calculationBasis = read.text(row, 'calculation_basis');
    const formulaExpression = read.text(row, 'governed_formula_expression');
    const formulaVersion = read.text(row, 'governed_formula_version');
    const resultRole = read.text(row, 'result_role');
    const displayName = read.text(row, 'display_name');
    const stateCode = read.text(row, 'state_code');
    const stateLabel = read.text(row, 'state_label');
    const wtr = wtrFromRow(read, row);

    return {
      metric_value: read.number(row, 'metric_value'),
      // An absent status cannot be assumed to mean `calculated`; the empty string is not
      // `CALCULATED_STATUS`, so the reading degrades rather than being trusted.
      calculation_status: read.text(row, 'calculation_status') ?? '',
      reason_code: read.text(row, 'reason_code'),
      source_dependency_status: read.text(row, 'source_dependency_status'),
      // Empty rather than absent so the delta gates below compare two strings and reject the pair on
      // window length (`Date.parse('')` is NaN) instead of matching two `undefined`s as adjacent.
      period_start: read.text(row, 'period_start') ?? '',
      period_end: read.text(row, 'period_end') ?? '',
      components: EXTRA_NUMERIC_COLUMNS.reduce(
        (bag, name) => ({ ...bag, [name]: read.number(row, name) }),
        {} as Record<string, number | null>
      ),
      // Spread rather than assigned: a metric with no confidence schema must keep the row it always
      // had, key for key, or 27 shipped Layer 1 cards gain a field their payload never mentioned.
      ...(confidence ? { confidence } : {}),
      ...(valueState ? { value_state: valueState } : {}),
      ...(entityType ? { entity_type: entityType } : {}),
      ...(entityRef ? { entity_ref: entityRef } : {}),
      ...(calculationBasis ? { calculation_basis: calculationBasis } : {}),
      ...(formulaExpression ? { governed_formula_expression: formulaExpression } : {}),
      ...(formulaVersion ? { governed_formula_version: formulaVersion } : {}),
      ...(resultRole ? { result_role: resultRole } : {}),
      ...(displayName ? { display_name: displayName } : {}),
      ...(stateCode ? { state_code: stateCode } : {}),
      ...(stateLabel ? { state_label: stateLabel } : {}),
      ...(wtr ? { wtr } : {})
    };
  });
};

export const CALCULATED_STATUS = 'calculated';

// A union, NOT `value: number | null`. The repo runs `strict: false`, so a nullable number is no
// protection at all — `null` is assignable to `number` and `value ?? 0` compiles silently, which is
// the same blind spot that let seven cards draw empty evidence chips for weeks. A missing property
// on a union arm is TS2339, and TS2339 is not gated by `strictNullChecks`, so this is the one shape
// that makes a spurious `0` a compile error rather than a code review.
//
// `metric_value` is null ON PURPOSE whenever `calculation_status` is not `calculated`; the value is
// withheld because it could not be governed, not because it is zero (FR § Data states). PLA-8815.
export type MetricResult =
  | {
      status: 'calculated';
      value: number;
      /**
       * The `EXTRA_NUMERIC_COLUMNS` this row actually carried a number for — the nulls are dropped,
       * so a consumer reading `components.eligible_customer_count` gets `number | undefined` and has
       * one absence to handle rather than two. Deliberately absent from the `unavailable` arm: a row
       * the warehouse could not calculate has no components either, by the same withheld-not-zero
       * rule that keeps `value` off that arm.
       */
      components: Record<string, number>;
      periodStart: string;
      periodEnd: string;
      sourceDependencyStatus: string | null;
      confidence?: MetricConfidence;
      valueState?: string | null;
      /**
       * The row's own subject, where the question answers per entity rather than per period:
       * `entityType` is the kind (`campaign`), `entityRef` the one (`cmp_751c43bcf989c1bf`).
       *
       * On both arms, and the reason is a detail row whose `metric_value` is null: it lands on
       * `unavailable` and would otherwise disappear from the card's evidence while its populated
       * siblings stayed. PLA-8828.
       */
      entityType?: string;
      entityRef?: string;
      /** `calculation_basis`, `governed_formula_*` and `result_role`, verbatim. See the row type. */
      calculationBasis?: string;
      formulaExpression?: string;
      formulaVersion?: string;
      resultRole?: string;
      /** `display_name`, `state_code` and `state_label`, verbatim. See the row type. */
      displayName?: string;
      stateCode?: string;
      stateLabel?: string;
      /** This row's "Why This Result" reason, on both arms for the same reason `confidence` is. */
      wtr?: WtrReason;
    }
  | {
      status: 'unavailable';
      // Kept verbatim rather than mapped to a display string: turning a code into a sentence needs
      // the catalogue's narrative templates and their named parameters, neither of which the six
      // columns carry (plan § III.6). Inventing copy here is how a placeholder ships as fact.
      calculationStatus: string;
      reasonCode: string | null;
      periodStart: string;
      periodEnd: string;
      sourceDependencyStatus: string | null;
      /**
       * On BOTH arms, unlike `components`. A withheld reading is exactly where
       * `confidence_limiting_reason` says something worth reading, so confidence is not the
       * property of a number the way a component count is. PLA-8825.
       */
      confidence?: MetricConfidence;
      valueState?: string | null;
      entityType?: string;
      entityRef?: string;
      calculationBasis?: string;
      formulaExpression?: string;
      formulaVersion?: string;
      resultRole?: string;
      displayName?: string;
      stateCode?: string;
      stateLabel?: string;
      wtr?: WtrReason;
    };

// A `calculated` row with no number is a contradiction the contract does not permit, so it is
// treated as unavailable rather than trusted — the alternative is `value: null` reaching an arm that
// promises a number.
const numbersOnly = (components: Record<string, number | null>): Record<string, number> =>
  Object.keys(components ?? {}).reduce(
    (kept, name) =>
      typeof components[name] === 'number' ? { ...kept, [name]: components[name] } : kept,
    {} as Record<string, number>
  );

export const toMetricResult = (row: MetricResultRow): MetricResult => {
  const shared = {
    periodStart: row.period_start,
    periodEnd: row.period_end,
    sourceDependencyStatus: row.source_dependency_status,
    ...(row.confidence ? { confidence: row.confidence } : {}),
    ...(row.value_state ? { valueState: row.value_state } : {}),
    ...(row.entity_type ? { entityType: row.entity_type } : {}),
    ...(row.entity_ref ? { entityRef: row.entity_ref } : {}),
    ...(row.calculation_basis ? { calculationBasis: row.calculation_basis } : {}),
    ...(row.governed_formula_expression
      ? { formulaExpression: row.governed_formula_expression }
      : {}),
    ...(row.governed_formula_version ? { formulaVersion: row.governed_formula_version } : {}),
    ...(row.result_role ? { resultRole: row.result_role } : {}),
    ...(row.display_name ? { displayName: row.display_name } : {}),
    ...(row.state_code ? { stateCode: row.state_code } : {}),
    ...(row.state_label ? { stateLabel: row.state_label } : {}),
    ...(row.wtr ? { wtr: row.wtr } : {})
  };

  if (row.calculation_status === CALCULATED_STATUS && typeof row.metric_value === 'number') {
    return {
      status: 'calculated',
      value: row.metric_value,
      components: numbersOnly(row.components),
      ...shared
    };
  }

  return {
    status: 'unavailable',
    calculationStatus: row.calculation_status,
    reasonCode: row.reason_code,
    ...shared
  };
};

// The `dimension_type` of the marker row every non-scalar response carries, and the reason code on
// it. It is the backend's own statement that this metric's grain is not scalar — "do not expect one
// `metric_value` row" — so it is named here rather than inferred, and filtered out below: it is a
// declaration ABOUT the response, not a reading in it. Feeding its `governed_non_scalar_grain` into
// a card's `insufficientData` reason is exactly the bug this parse replaces.
export const NON_SCALAR_MARKER_DIMENSION = 'metric_status';
export const NON_SCALAR_GRAIN_REASON = 'governed_non_scalar_grain';

export interface MetricDistribution {
  /** `dimension_type` of the rows the segments came from — `lifecycle_stage`, `holding_count_band`. */
  dimension: string;
  segments: DistributionSegment[];
  /**
   * The `EXTRA_NUMERIC_COLUMNS` this response carried at POPULATION level — the same value on every
   * calculated row, because it describes the whole cohort rather than one segment of it. A column
   * whose readings disagree across rows is dropped rather than resolved by row order.
   */
  components: Record<string, number>;
}

/**
 * The grouping columns the governed REVERSAL breakdowns split on, one per metric.
 *
 * O-REV-004, O-REV-005 and O-REV-007 are segment sets by every measure that matters — one row per
 * category, a share per row — but they answer a schema of their own rather than the
 * `dimension_type`/`distribution_key`/`distribution_value` triple the rest of the page uses, so
 * `distributionFromEnvelope`'s detection above cannot see them. They carry no `dimension_type` to
 * name the split and no `calculation_status` to filter on either; what identifies the shape is
 * `share_of_events_pct` beside exactly one of these three names.
 *
 * Named rather than inferred as "the second column": a grouping column has to be recognised, and
 * taking one by position would read `tenant_id` as a category the moment a question reorders its
 * projection. Captured from `cols` on `api-uat.perxtech.io` `generic` 2026-09-18 — all three
 * answered ZERO rows, so no cell of this shape has been observed populated. PLA-8828.
 */
export const REVERSAL_BREAKDOWN_KEY_COLUMNS = [
  'entitlement_type',
  'reason_code',
  'reconciliation_status'
] as const;

const REVERSAL_BREAKDOWN_SHARE_COLUMN = 'share_of_events_pct';

/**
 * A governed reversal breakdown's rows as a `MetricDistribution`, or `null` for any other shape.
 *
 * `dimension` is the grouping COLUMN's own name, which is what the wire states about the split, and
 * is the same thing `distributionFromEnvelope` puts there for the standard shape.
 *
 * `null` rather than an empty distribution when nothing grouped, for the reason the standard parse
 * gives: a bar of nothing reads as a measured nothing.
 */
const reversalBreakdownFromEnvelope = (envelope: MetricEnvelope): MetricDistribution | null => {
  const cols = envelope?.payload?.data?.cols ?? [];
  const indexOf = (name: string): number => columnIndexOf(cols, name);

  const shareAt = indexOf(REVERSAL_BREAKDOWN_SHARE_COLUMN);
  const keyed = REVERSAL_BREAKDOWN_KEY_COLUMNS.map(name => [name, indexOf(name)] as const).filter(
    ([, at]) => at !== -1
  );

  // Exactly one, never the first of several: two grouping columns on one response is a schema this
  // parse has not seen, and picking one of them would draw a split the question did not answer.
  if (shareAt === -1 || keyed.length !== 1) {
    return null;
  }

  const [dimension, keyAt] = keyed[0];
  const rows = readingRowsOnly(readerFor(cols), envelope?.payload?.data?.rows ?? []);
  const countAt = indexOf('n_events');
  const cell = (row: unknown[], at: number): unknown => (at === -1 ? undefined : row[at]);

  const segments = rows.reduce<DistributionSegment[]>((kept, row) => {
    const key = asString(cell(row, keyAt));
    const value = asNumber(cell(row, shareAt));
    const count = asNumber(cell(row, countAt));

    if (key === null || value === null) {
      return kept;
    }

    return [...kept, count !== null ? { key, value, count } : { key, value }];
  }, []);

  return segments.length > 0 ? { dimension, segments, components: {} } : null;
};

/**
 * A DISTRIBUTION metric's rows, parallel to `rowsFromEnvelope` and never instead of it.
 *
 * O-COM-031 and O-COM-032 answer with one row per lifecycle stage / holding band and carry no
 * `metric_value` column at all, so the scalar parse above reads every one of their rows as
 * `unavailable` — a card whose only live metric is a distribution then reports `insufficientData`
 * with `calculated` as its stated reason. This is the read path for that shape; the 95 genuinely
 * scalar metrics keep the one above untouched.
 *
 * Detection is the COLUMN set, not the marker row: `distribution_key`/`distribution_value` are what
 * make the parse possible at all, and a scalar metric's question never returns them, so there is no
 * false positive to guard against. The marker row is the backend's own contract statement and is
 * used for what it states — it is excluded from the segments by its `dimension_type`, so its
 * `enabled_not_aggregated` status can never be mistaken for a withheld reading.
 *
 * Falls through to the governed reversal-breakdown schema above when the triple is absent, so one
 * call answers for every segment set this endpoint serves and no caller has to know which.
 *
 * `null` when the response is not a distribution shape, or is one with no calculated rows —
 * withheld, not an empty distribution, which would draw a bar of nothing. PLA-8815, PLA-8828.
 */
export const distributionFromEnvelope = (envelope: MetricEnvelope): MetricDistribution | null => {
  const cols = envelope?.payload?.data?.cols ?? [];
  // Reason rows out first, as everywhere else on this endpoint. `rowsFromEnvelope` sorts them to the
  // end, which is enough for the scalar path; this parse reads the RAW rows, so it has to partition.
  // They declare no `dimension_type` today and would fall out of `readings` below anyway; the
  // partition is what stops that being an accident of one response's null. PLA-8828.
  const rows = readingRowsOnly(readerFor(cols), envelope?.payload?.data?.rows ?? []);
  const indexOf = (name: string): number => columnIndexOf(cols, name);

  const dimensionAt = indexOf('dimension_type');
  const keyAt = indexOf('distribution_key');
  const valueAt = indexOf('distribution_value');

  if (dimensionAt === -1 || keyAt === -1 || valueAt === -1) {
    // The one other segment-set schema this endpoint answers with. Tried only after the standard
    // triple has been ruled out, so a response carrying both could never be read the second way.
    return reversalBreakdownFromEnvelope(envelope);
  }

  const countAt = indexOf('n_customers');
  const labelAt = indexOf('display_name');
  const statusAt = indexOf('calculation_status');
  const extraAt = EXTRA_NUMERIC_COLUMNS.map(name => [name, indexOf(name)] as const);
  const cell = (row: unknown[], at: number): unknown => (at === -1 ? undefined : row[at]);
  const dimensionOf = (row: unknown[]): string | null => asString(cell(row, dimensionAt));

  const readings = rows.filter(row => {
    const dimension = dimensionOf(row);

    return dimension !== null && dimension !== NON_SCALAR_MARKER_DIMENSION;
  });
  // Whatever the real rows declare, never a hardcoded name: the two shipped distributions split on
  // different dimensions and a third will split on another. Rows of a SECOND dimension are dropped
  // rather than concatenated — one bar drawn from two dimensions sums to 200% and reads as one.
  const dimension = readings.length > 0 ? dimensionOf(readings[0]) : null;

  if (dimension === null) {
    return null;
  }

  // Same withheld-not-zero discipline as the scalar path: a stage the warehouse could not calculate
  // contributes nothing to the bar, rather than a zero-width segment that reads as a measured zero.
  const calculated = readings.filter(
    row => dimensionOf(row) === dimension && asString(cell(row, statusAt)) === CALCULATED_STATUS
  );

  const segments = calculated.reduce<DistributionSegment[]>((kept, row) => {
    const key = asString(cell(row, keyAt));
    const value = asNumber(cell(row, valueAt));
    const count = asNumber(cell(row, countAt));
    const label = asString(cell(row, labelAt));

    if (key === null || value === null) {
      return kept;
    }

    return [
      ...kept,
      { key, value, ...(count !== null ? { count } : {}), ...(label !== null ? { label } : {}) }
    ];
  }, []);

  if (segments.length === 0) {
    return null;
  }

  const components = extraAt.reduce((bag, [name, at]) => {
    const perRow = calculated.map(row => asNumber(cell(row, at)));
    const first = perRow[0];

    // Read off row 0 only once every other calculated row agrees. A column that varies by row is
    // per-SEGMENT data this shape has no slot for, and picking one row's figure to stand for the
    // whole population would report a number nothing measured.
    return typeof first === 'number' && perRow.every(reading => reading === first)
      ? { ...bag, [name]: first }
      : bag;
  }, {} as Record<string, number>);

  return { dimension, segments, components };
};

const DELTA_UNITS: Record<MetricFormat, DeltaUnit> = {
  // A movement between two percentages is measured in percentage POINTS, never in per cent.
  percent: 'pp',
  count: 'count',
  currency: 'currency',
  points: 'points',
  ratio: 'ratio'
};

// Only counts and currency print a second figure, so only they get one built (the renderer gates on
// the same two units). A `pp` primary already IS the percentage-point movement.
const UNITS_WITH_SECONDARY: DeltaUnit[] = ['count', 'currency'];

// What a RISE in this metric means for the reader. No arithmetic can know that a rise in at-risk
// customers is bad news, so the descriptor states it rather than the normaliser guessing.
export type RiseSentiment = MetricDelta['sentiment'];

export interface LiveMetricDescriptor {
  /** The registry `metricKeys` entry this tile renders under. */
  key: string;
  label: string;
  format: MetricFormat;
  riseMeans: RiseSentiment;
  /**
   * This metric's reading is the WORD the row states in `value_state`, not a figure in
   * `metric_value` -- the catalogue's `Text status badge` shape, which `C-INS-001` (Policy Status /
   * Health) is the one bound instance of.
   *
   * Declared per identity rather than inferred from `value_state` being present, because on every
   * other metric that column is a BAND QUALIFYING a figure and not the figure itself: `C-COM-003`
   * answers `value_state: 'low'` beside `metric_value: -41.84` and `C-HYB-006` answers `'dominant'`
   * beside `50.0` (`api-uat.perxtech.io`, tenant `generic`). Reading the word onto every tile that
   * carries one would replace those numbers with their band names. PLA-8828.
   */
  reading?: 'categorical';
  /**
   * The governed words a NUMERIC status code stands for, by code.
   *
   * `O-COM-006` (Integration Health Status) answers `metric_value: 2.0` meaning UNHEALTHY, and the
   * tile drew `2` between a Data Freshness Score of 31.4 and a Source Coverage Score of 100 — a
   * rollup verdict reading as the worst score on the card. Its catalogue Information Format is
   * `Text status badge — effective timestamp and main reason` (Metrics Mapping with UI FR - Ov,
   * Excel row 22).
   *
   * Declared HERE rather than derived at runtime from the row's own
   * `formula_display_text: '...; 0=HEALTHY 1=DEGRADED 2=UNHEALTHY'`: that column is deliberately
   * unread (see `MetricResultRow`), and a sentence is not a contract. A code with no entry prints
   * nothing, because a status vocabulary that has grown is not a number to fall back on. PLA-8828.
   */
  statusCodes?: Record<number, string>;
}

// The shorter window as a fraction of the longer one, above which the two count as the same grain.
//
// NOT an absolute tolerance, which is the mistake this replaces: consecutive CALENDAR MONTHS differ
// by one to three days, so any tolerance tight enough to catch a grain change rejects every real
// month-over-month comparison — and the contract's own example is monthly. That defect suppressed
// every movement on the page and failed silently, because a suppressed delta draws no chip and
// nothing errors.
//
// 0.75 sits in a wide gap with no legitimate case near it. Worst legitimate: Feb against Mar, 0.903
// (a leap year against a common one is 0.997). Nearest illegitimate: a month against a quarter,
// 0.337, and a month against a week, 0.226. A 28-day period against a 31-day month also passes at
// 0.903 — accepted deliberately, since both are "about a month" and no column states the grain
// finely enough to tell them apart. PLA-8815.
const SAME_GRAIN_RATIO = 0.75;

const spanOf = (result: MetricResult): number =>
  Date.parse(result.periodEnd) - Date.parse(result.periodStart);

/**
 * The movement between two consecutive periods, or nothing.
 *
 * The contract supplies no previous-period parameter and no comparison basis, so a delta can only
 * come from comparing `row[0]` with `row[1]` — and three things have to hold before those two rows
 * are a movement rather than two unrelated numbers. Each gate returns `undefined`, which the tile
 * already renders correctly (`StatBox` draws no chip without a delta), so a suppressed movement
 * costs no UI.
 *
 * NOTE this is a floor, not the answer. `Screen Filters` makes Comparison Period a REQUIRED filter
 * whose behaviour cell reads "changes only the comparison used for trend or variance", and three of
 * its four options are unreachable from adjacent rows at all. The governed narratives need
 * `{comparison_period}` as a NAME, which no arrangement of these rows can produce (plan § IV.2).
 * PLA-8815.
 */
export const deltaFrom = (
  current: MetricResult,
  previous: MetricResult | undefined,
  descriptor: LiveMetricDescriptor
): MetricDelta | undefined => {
  // Gate 1 — a second period may simply not exist: a newly onboarded tenant, or a metric deployed
  // this period, has exactly one row.
  if (!previous) {
    return undefined;
  }

  // Gate 2 — the basis has to be a reading. An uncalculated previous period has no `metric_value` by
  // the contract's own rule, so a movement against it would be the fabricated figure the
  // never-show-zero rule exists to prevent.
  if (current.status !== 'calculated' || previous.status !== 'calculated') {
    return undefined;
  }

  // Gate 3 — ordering is not adjacency. `ORDER BY period_end DESC` guarantees `row[1]` is EARLIER,
  // not that it is the period before; a skipped period would silently become "the previous period".
  // The windows are half-open, so consecutive periods meet end-to-start.
  if (previous.periodEnd !== current.periodStart) {
    return undefined;
  }

  // ...and comparing a month against a week is not a movement either. No column states the period
  // grain, so the two windows are compared rather than looked up — which is what makes this safe
  // while the grain is unknown. Compared as a RATIO, not a difference: see `SAME_GRAIN_RATIO`.
  const span = spanOf(current);
  const previousSpan = spanOf(previous);

  if (!Number.isFinite(span) || !Number.isFinite(previousSpan) || span <= 0 || previousSpan <= 0) {
    return undefined;
  }

  if (Math.min(span, previousSpan) / Math.max(span, previousSpan) < SAME_GRAIN_RATIO) {
    return undefined;
  }

  const value = current.value - previous.value;
  const unit = DELTA_UNITS[descriptor.format];
  const direction = value === 0 ? 'flat' : value > 0 ? 'up' : 'down';

  const rise = descriptor.riseMeans;
  const fall: RiseSentiment =
    rise === 'positive' ? 'negative' : rise === 'negative' ? 'positive' : 'neutral';
  const sentiment: RiseSentiment =
    direction === 'flat' ? 'neutral' : direction === 'up' ? rise : fall;

  // A flat movement carries no second figure. Its relative change is genuinely 0, so printing it is
  // not WRONG -- but the chip already says `0`, and `0 (0%)` states the same fact twice. It is also
  // the only way the figure could be inconsistent: a flat movement from a previous value of 0 has no
  // relative expression at all, so some flat chips would carry `(0%)` and others nothing, decided by
  // a number the reader cannot see. PLA-8815.
  const secondary =
    direction === 'flat' || UNITS_WITH_SECONDARY.indexOf(unit) === -1
      ? undefined
      : secondaryFor(current.value, previous.value);

  return { value, unit, direction, sentiment, ...(secondary ? { secondary } : {}) };
};

/**
 * The one confidence a metric published, taken from whichever of its rows carries it.
 *
 * Confidence is POPULATION-level on this wire, not per-row: O-COM-020's five rows all read
 * `confidence_score=0.9141` / `IDENTITY_MATCH_PARTIAL`, and O-COM-009's four all read `0.9208`
 * (UAT `generic`, 2026-09-08). So there is one answer per metric and the only question is which row
 * to read it off.
 *
 * The first row with a FINITE score wins, falling back to the first row that declared the columns at
 * all. Not simply `results[0]`, because the non-scalar marker row is not always last — O-COM-020 and
 * O-COM-025 both put it first — and on O-COM-023 that row carries the five columns with every value
 * null. Reading row 0 there would report "confidence withheld" over a live 0.9129. PLA-8815.
 */
/**
 * A quotient of two readings, as a percentage, rounded to two decimals.
 *
 * Two decimals is the DATA precision and not the display one: `components/Intelligence/formatMetric`
 * rounds every `percent` tile to a single decimal on its way to the screen (`roundToOneDecimal`), so
 * 95.12 prints as `95.1%`. The rounding here exists only so the stored figure is the quotient rather
 * than its floating-point residue — 2378/2500*100 evaluates to 95.11999999999999 — and it leaves the
 * display authority where it already is rather than adding a second one.
 *
 * Here rather than in `assembleCard.ts`, where it was written, because Product Readiness derives a
 * rate from its own aggregated rows and `assembleCard` imports THAT module: one shared definition is
 * what keeps a tile and the rate beside it rounding the same way. PLA-8815.
 */
export const asPercentage = (numerator: number, denominator: number): number =>
  Math.round((numerator / denominator) * 10000) / 100;

export const confidenceOf = (results: MetricResult[]): MetricConfidence | undefined => {
  const declared = (Array.isArray(results) ? results : [])
    .map(result => result.confidence)
    .filter((confidence): confidence is MetricConfidence => confidence !== undefined);

  return (
    declared.find(
      confidence => typeof confidence.score === 'number' && Number.isFinite(confidence.score)
    ) ?? declared[0]
  );
};

/**
 * The metric's `calculation_basis`, off the first row that carried a non-empty one.
 *
 * The wire's own row order decides, and it genuinely varies in what it puts first: on O-COM-009 the
 * segment rows lead with the per-band basis and the marker row states the metric-level one, while on
 * O-COM-020 the marker leads. Picking a preferred row would be a rule the catalogue does not state,
 * so this takes the wire in the order the wire sent it. PLA-8815.
 *
 * STATUS IS NOT READ HERE, and one caller cares: a row that answered `insufficient_data` still
 * contributes its basis, so a card can carry a live sentence while showing no reading. The demo
 * layer's rescue branch (`demoFallback.ts` § `withDemoFallback`) then drops that sentence when it
 * draws over the failure — correctly, since the sentence explained the absence, but the live text is
 * gone and nothing puts it back. PLA-8828.
 */
export const calculationBasisOf = (results: MetricResult[]): string | undefined =>
  (Array.isArray(results) ? results : [])
    .map(result => result.calculationBasis)
    .find(basis => typeof basis === 'string' && basis.trim() !== '');

/**
 * Every "Why This Result" reason this metric's rows carried, ranked lowest-`selectionRank`-first —
 * the order the wire itself asks a reader to use ("final ordering after dedup/selection"), with
 * `wtr_priority` breaking a tie and wire order breaking that. A reason with no rank sorts after
 * every ranked one rather than first, on either key, since `null` is not rank zero. Most metrics
 * answer with none at all: WTR runtime coverage is partial today (see `data/wtr.ts`), and a metric
 * outside it simply has no `wtr` row to collect here.
 */
export const wtrReasonsOf = (results: MetricResult[]): WtrReason[] => {
  const rank = (value: number | null): number =>
    typeof value === 'number' ? value : Number.POSITIVE_INFINITY;

  return (Array.isArray(results) ? results : [])
    .map(result => result.wtr)
    .filter((wtr): wtr is WtrReason => wtr !== undefined)
    .map((wtr, index) => ({ wtr, index }))
    .sort(
      (left, right) =>
        rank(left.wtr.selectionRank) - rank(right.wtr.selectionRank) ||
        rank(left.wtr.priority) - rank(right.wtr.priority) ||
        left.index - right.index
    )
    .map(entry => entry.wtr);
};

/**
 * The ONLY place a live `CardMetric` is built.
 *
 * Everything downstream of here renders; nothing downstream decides. The union above is what makes
 * that hold: on the `unavailable` arm there is no `value` to reach, so the fallback that would
 * print `0` for "we could not calculate this" cannot be written, not merely must not be.
 *
 * An unavailable reading carries no `status` and no `valueText` either. Both are display strings,
 * and the sentence a reason code turns into is governed copy the six columns do not carry — the
 * tile prints `—` and the card's own data state does the explaining (plan § II.7), which is what
 * `unavailableStatus`/`unavailableReasonCode` are for: not display strings themselves, but what
 * `assembleCard` needs to tell a real backend answer apart from nothing having been fetched. PLA-8815.
 */
export const toCardMetric = (
  descriptor: LiveMetricDescriptor,
  results: MetricResult[],
  /**
   * Reads this named `EXTRA_NUMERIC_COLUMNS` entry off the current row instead of `metric_value`.
   * A separate ARGUMENT rather than a field on `LiveMetricDescriptor`, so every existing caller —
   * every plain tile on all 27 cards — resolves through the identical code path it always did and
   * the component case is visible at the one call site that passes it.
   */
  component?: string,
  /**
   * This metric's "Why This Result" reasons, already ranked by `wtrReasonsOf`. A separate ARGUMENT
   * rather than a field read off `results` here, because a reason belongs to the METRIC and not to
   * one of its rows: the caller has already collected them across every row the question answered,
   * and re-reading them per row would make "which row's reasons" a question with no answer.
   * PLA-8828.
   */
  reasons?: WtrReason[]
): CardMetric => {
  // The metric's governed pair, carried on EVERY arm below including the withheld ones. A tile with
  // no reading still knows how confident the pipeline was and why, and the card's pill is an average
  // over metrics rather than over the tiles that happened to resolve. Spread on presence, so a tile
  // reading an identifier that declares neither column is the object it always was. The tile RENDERS
  // this; it never derives it (PLA-8825 § G32 — the server's score and band are authority).
  //
  // Read across the metric's rows and not off `results[0]`: both layers UNION a marker or status row
  // into the same answer, and on either one row 0 can be the row that carries the columns empty. The
  // single-row case is unchanged — `confidenceOf([current])` is `current.confidence`.
  const confidence = confidenceOf(results);
  const basis = calculationBasisOf(results);
  const governed = {
    ...(confidence ? { confidence } : {}),
    ...(basis ? { whyThisResult: basis } : {})
  };
  // Spread on presence for the same reason `governed` is: a metric with no reason rows keeps the
  // object 27 shipped cards assert on, key for key, rather than gaining an empty array.
  const base = {
    key: descriptor.key,
    label: descriptor.label,
    format: descriptor.format,
    ...governed,
    ...(reasons && reasons.length > 0 ? { reasons } : {})
  };
  const current = results[0];

  if (!current) {
    return { ...base, value: null };
  }

  // Raw, never mapped: this file's rule is that everything downstream renders and nothing downstream
  // decides, so the tone or stage a state implies is the tile's to resolve, not this parser's.
  const withValueState = {
    ...(current.valueState ? { valueState: current.valueState } : {}),
    ...(current.stateLabel ? { stateLabel: current.stateLabel } : {})
  };

  // The governed DEFINITION, stated by the row rather than hardcoded on the tile. Carried whether or
  // not the reading resolved -- a withheld reading is exactly where the reader wants to know what was
  // being computed. It lands on `formulaExpression`, never on `formula`: `formula` is the tile's own
  // authored caption and rerouting the wire through it overwrites what the tile meant to say
  // (PLA-8815). PLA-8828.
  const withGovernance = {
    ...(current.formulaExpression ? { formulaExpression: current.formulaExpression } : {}),
    ...(current.formulaVersion ? { formulaVersion: current.formulaVersion } : {})
  };

  /**
   * A `Text status badge` metric's reading: the WORD the row states, printed as the tile's own
   * reading rather than beside it.
   *
   * It takes its own arm because the row that carries it has NO `metric_value`, and `toMetricResult`
   * classifies a calculated row with no number as `unavailable` -- for every numeric metric that is
   * a contradiction the contract does not permit, and for this one it is the normal answer. So the
   * test is `calculation_status`, not the parsed arm: a status the backend really did withhold falls
   * through to the branch below and carries no display string, exactly as before.
   *
   * `StatBox` prints `valueText` when a tile has one and formats `value` otherwise -- it has never
   * read `valueState` -- which is why `C-INS-001` drew the empty-reading placeholder over a live
   * `Active`. The word is passed through verbatim, and `valueState` still rides alongside it, so the
   * consumers already reading the raw column (`CustomerProfileSection`) are untouched. PLA-8828.
   */
  if (descriptor.reading === 'categorical') {
    const calculated =
      current.status === 'calculated' || current.calculationStatus === CALCULATED_STATUS;

    if (calculated && current.valueState) {
      return {
        ...base,
        value: current.status === 'calculated' ? current.value : null,
        valueText: current.valueState,
        ...withValueState,
        ...withGovernance
      };
    }
  }

  /**
   * The same treatment for a status the wire states as a CODE rather than as a word — the reading
   * is `statusCodes[metric_value]`, through the identical `valueText` slot.
   *
   * `value` is dropped on this arm rather than carried alongside the word: the code is the word's
   * encoding and not a quantity, so a delta chip or a progress track built from it would compare
   * two verdicts as if UNHEALTHY were twice DEGRADED. An unmapped code takes the no-reading arm
   * below, which prints the dash. PLA-8828.
   */
  if (descriptor.statusCodes && current.status === 'calculated') {
    const word = descriptor.statusCodes[current.value];

    return {
      ...base,
      value: null,
      ...(word ? { valueText: word } : {}),
      ...withValueState,
      ...withGovernance
    };
  }

  if (current.status !== 'calculated') {
    return {
      ...base,
      value: null,
      unavailableStatus: current.calculationStatus,
      unavailableReasonCode: current.reasonCode,
      ...withValueState,
      ...withGovernance
    };
  }

  if (component !== undefined) {
    const reading = current.components[component];

    // A calculated row that simply did not carry the column: withheld, not zero, and NOT
    // `unavailableStatus` either — the backend answered, and it answered about `metric_value`.
    // No delta: `deltaFrom` compares `value`, which is `metric_value` for both rows, so a movement
    // built here would be the wrong metric's movement printed under this tile's label.
    return typeof reading === 'number'
      ? { ...base, value: reading, ...withValueState, ...withGovernance }
      : { ...base, value: null, ...withValueState, ...withGovernance };
  }

  const delta = deltaFrom(current, results[1], descriptor);

  return {
    ...base,
    value: current.value,
    ...(delta ? { delta } : {}),
    ...withValueState,
    ...withGovernance
  };
};
